import { CheckCircle2, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { pluralCategory } from '../validation/VerdictHeader'

/** Die drei Lagen eines Laufs: Datei erzeugt, Beleg gelesen, gescheitert. */
export type ConversionOutcome = 'created' | 'read' | 'failed'

export interface ConversionResultHeaderProps {
  outcome: ConversionOutcome
  /** Was herauskam — `PDF`, der Katalogname des Zielformats (`XRechnung 3.0.2`) oder `JSON`. */
  formatLabel?: string
  /** Groesse des Ergebnisses in Bytes. */
  bytes?: number
  /** Zahl der Hinweiszeilen im Report darunter (zusammengefasst). */
  warningCount: number
  /** Zahl der Fehlerzeilen im Report darunter (zusammengefasst). */
  errorCount: number
  /** Am Kontingent abgewiesen — die Kopfzeile nennt das statt einer Fehlerzahl. */
  quotaExceeded?: boolean
}

/**
 * Das Ergebnis eines Laufs als Kopfzeile — „Erzeugt — XRechnung 3.0.2 · 12 KB · 2 Hinweise" / „Fehlgeschlagen —
 * 2 Fehler". Gleiche Bauform wie `VerdictHeader` der Validierung: Symbol + Wort kraeftig, Begruendung leiser, die
 * Statusfarbe NUR am Symbol und an der 2-px-Kante — Farbe nie allein (Symbol und Wort sagen dasselbe).
 */
export function ConversionResultHeader({
  outcome,
  formatLabel,
  bytes,
  warningCount,
  errorCount,
  quotaExceeded = false,
}: ConversionResultHeaderProps) {
  const { t, i18n } = useTranslation()
  const failed = outcome === 'failed'
  const Icon = failed ? XCircle : CheckCircle2

  const label: Record<ConversionOutcome, string> = {
    created: t('invoiceConversionPage.result.created'),
    read: t('invoiceConversionPage.result.read'),
    failed: t('invoiceConversionPage.result.failed'),
  }

  const parts: string[] = []
  if (!failed && formatLabel) parts.push(formatLabel)
  if (!failed && bytes !== undefined)
    parts.push(
      t('invoiceConversionPage.result.size', { kb: Math.max(1, Math.round(bytes / 1024)) })
    )
  if (failed && quotaExceeded) parts.push(t('invoiceConversionPage.result.quota'))
  if (failed && !quotaExceeded && errorCount > 0)
    parts.push(
      t(`invoiceConversionPage.result.errors.${pluralCategory(i18n.language, errorCount)}`, {
        n: errorCount,
      })
    )
  if (warningCount > 0)
    parts.push(
      t(`invoiceConversionPage.result.warnings.${pluralCategory(i18n.language, warningCount)}`, {
        n: warningCount,
      })
    )

  return (
    <div
      // 🔴 `green-800`, nicht `success`: `success` hatte auf Weiss 2.98:1 (gemessen 2026-10-10, Validierung).
      className={`border-t-2 pt-3 ${failed ? 'border-destructive' : 'border-green-800'}`}
      data-testid="invoice-conversion-result-header"
      data-outcome={outcome}
    >
      <h2 className="flex items-start gap-2 text-2xl font-semibold tracking-tight">
        <Icon
          className={`mt-1 h-6 w-6 shrink-0 ${failed ? 'text-destructive' : 'text-green-800'}`}
          aria-hidden="true"
        />
        <span className="min-w-0 [overflow-wrap:anywhere]">
          <span data-testid="invoice-conversion-result-label">{label[outcome]}</span>
          {parts.length > 0 && (
            <span
              className="font-normal text-muted-foreground tabular-nums"
              data-testid="invoice-conversion-result-detail"
            >
              {` — ${parts.join(' · ')}`}
            </span>
          )}
        </span>
      </h2>
    </div>
  )
}
